// Resolving an action's `expected_response` to something a live socket message
// can be compared against.
//
// The comparison itself is NOT defined here. `debounceKeyForEncoded` is the
// codebase's canonical message identity — it packs (deviceType, MessageContent,
// enumCode) by walking the full union hierarchy, and the dashboard's debounce
// path has depended on it being right for every category since it was written.
// "Is this the message the action said to expect?" is exactly "do these two
// messages have the same identity", so this module only builds the SAME packed
// key for the catalog entry the operator picked, using the same `debounceKey`
// packer, and compares numbers. A second notion of message identity beside it
// is the one thing that could drift.
import { MessageT } from "../../../flatbuffers/generated/ln2/message";
import {
  debounceKeyForEncoded,
  messageContentFromTypeId,
} from "../../../flatbuffers/decoders/frontEndMessageDecoder";
import {
  debounceKey,
  DEVICE_ELEVATOR,
  DEVICE_ESCALATOR,
  DEVICE_STATUS,
} from "../../../utils/debounceLookup";
import { MessageInterface } from "../../../interfaces/Message.interface";
import { SocketMessageInterface, WithheldMark } from "../../../interfaces/Monitoring.interface";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";

/**
 * The device types a live message's identity can carry.
 *
 * The message catalog is schema-driven (`useSchemaMessages`) and every entry in
 * it is `deviceType: "ALL"` — the generated enums are shared across device
 * types and the wrapper is only applied downstream, when the bank is known. So
 * the operator picks a (category, member) pair and NOT a device, and an expected
 * response must match whichever device wrapper the controller happens to send it
 * under. Enumerating the three the packer can produce keeps the comparison an
 * exact key match rather than a masked one, which would quietly widen what
 * counts as the same message.
 */
const DEVICE_CANDIDATES: readonly number[] = [DEVICE_ELEVATOR, DEVICE_ESCALATOR, DEVICE_STATUS];

/** One nominated message, and every packed identity that would satisfy it. */
export interface ExpectedResponseIdentity {
  /** The id exactly as stored on the action, so it can key an observed-set. */
  id: string;
  /**
   * The identities that count as this message arriving. EMPTY when the id
   * resolves to no catalog entry — see `expectedResponseIdentities`.
   */
  keys: ReadonlySet<number>;
}

/**
 * The enum a catalog entry carries, under either spelling.
 *
 * `useSchemaMessages` writes `enumId` / `messageTypeId`; rows that predate it
 * carry the snake_case pair. `MessageInterface`'s index signature means a wrong
 * guess is a silent `undefined` rather than a compile error, so probe both —
 * the same reasoning `resolveMessageEnum` documents.
 */
type CatalogEntry = {
  enumId?: number | null;
  enum_id?: number | null;
  messageTypeId?: number | null;
  message_type_id?: number | null;
};

/**
 * Resolve nominated message ids to identities, one entry per id, in order.
 *
 * An id that matches no catalog entry keeps its place with an EMPTY key set
 * rather than being dropped. Dropping it would shrink the set every nominated
 * message is checked against, so an action whose expected response was
 * misconfigured would report Confirmed off the messages that DID resolve — the
 * exact overstatement this feature exists to remove. An empty set can never be
 * satisfied, so such an action reports Unconfirmed, which is the truth.
 */
export function expectedResponseIdentities(
  messages: MessageInterface[],
  ids: readonly string[],
): ExpectedResponseIdentity[] {
  return ids.map((id) => {
    const entry = messages.find((m) => String(m.id) === id) as CatalogEntry | undefined;
    const enumValue = entry?.enumId ?? entry?.enum_id;
    const typeId = entry?.messageTypeId ?? entry?.message_type_id;
    if (typeof enumValue !== "number" || typeof typeId !== "number") {
      return { id, keys: new Set<number>() };
    }
    const content = messageContentFromTypeId(typeId);
    return {
      id,
      keys: new Set(DEVICE_CANDIDATES.map((device) => debounceKey(device, content, enumValue))),
    };
  });
}

/**
 * The identity of a message that just arrived on the socket, or undefined for
 * one the decoder does not classify.
 *
 * `SocketMessageInterface.msg` is the encoded union plus two fields of its own,
 * so the union halves are rebuilt into a real `MessageT` rather than cast: the
 * generated class is what `debounceKeyForEncoded` is typed against, and a cast
 * would let a shape change pass the compiler.
 */
export function socketMessageIdentity(msg: SocketMessageInterface): number | undefined {
  const encoded = msg.msg;
  if (!encoded) return undefined;
  return debounceKeyForEncoded(new MessageT(encoded.contentType, encoded.content));
}

/**
 * The identity of the command a Withheld verdict held back, or undefined when
 * the lift carries no withheld mark.
 *
 * A Withheld frame deliberately leaves `msg` alone — the command never happened,
 * so it is not the lift's last message — which means `socketMessageIdentity`
 * would answer with whatever arrived before it. The command is read from the
 * newest withheld mark instead, through the same `debounceKey` packer. Only an
 * Elevator Control command leaves a mark, which is why the device is fixed.
 */
export function withheldCommandIdentity(msg: SocketMessageInterface): number | undefined {
  const newest = (msg.withheldCalls ?? []).reduce<WithheldMark | undefined>(
    (latest, mark) => (latest === undefined || mark.atMs >= latest.atMs ? mark : latest),
    undefined,
  );
  if (newest === undefined) return undefined;
  return debounceKey(DEVICE_ELEVATOR, MessageContent.Control_ControlMessage, newest.control);
}

/**
 * The nominated ids this message satisfies.
 *
 * ACTIVE only. A delta carries `active: false` when a message CLEARS, and an
 * action's expected response is "this became true", never "this stopped being
 * true" — counting a clear would let an action confirm off the controller
 * dropping the very state it was asked to produce.
 */
export function observedExpectedIds(
  identities: readonly ExpectedResponseIdentity[],
  msg: SocketMessageInterface,
): string[] {
  if (!msg.msg?.active) return [];
  const key = socketMessageIdentity(msg);
  if (key === undefined) return [];
  return identities.filter((identity) => identity.keys.has(key)).map((identity) => identity.id);
}
